import { action, computed, observable } from 'mobx';
import { Features, IFeatures } from '../internal/constants/Features';
import { defaultFragments, QueryType } from './features/Default';
import { mandatoryFragments } from './features/Mandatory';
import { productLargeImagesFragments } from './features/images/ProductLargeImages';
import { productMediumImagesFragments } from './features/images/ProductMediumImages';
import { productSmallImagesFragments } from './features/images/ProductSmallImages';
import { skuLargeImagesFragments } from './features/images/SkuLargeImages';
import { skuMediumImagesFragments } from './features/images/SkuMediumImages';
import { skuSmallImagesFragments } from './features/images/SkuSmallImages';
import { skuSmooshImagesFragments } from './features/images/SkuSmooshImages';
import { skuModelImagesFragments } from './features/images/SkuModelImages';
import { autoReplenishFragments } from './features/productDisplay/AutoReplenish';
import { benefitFragments } from './features/productDisplay/Benefit';
import { brandFragments } from './features/productDisplay/Brand';
import { colorFamilyFragments } from './features/productDisplay/ColorFamily';
import { engravingFragments } from './features/tools/Engraving';
import { finishOptionsFragments } from './features/filters/FinishOptions';
import { finishFragments } from './features/filters/Finish';
import { formFragments } from './features/Form';
import { fragranceFamilyFragments } from './features/productDisplay/FragranceFamily';
import { keyIngredientsFragments } from './features/filters/KeyIngredient';
import { locationFragments } from './features/tools/Location';
import { maincatFragments } from './features/filters/Maincat';
import { moodFragments } from './features/filters/Mood';
import { occasionFragments } from './features/filters/Occasion';
import { perlgemFragments } from './features/productDisplay/Perlgem';
import { productCollectionFragments } from './features/ProductCollection';
import { productCoverageFragments } from './features/productDisplay/ProductCoverage';
import { productCrossSellFragments } from './features/tools/ProductCrossSell';
import { productFinishFragments } from './features/filters/ProductFinish';
import { productReviewsFragments } from './features/productDisplay/ProductReviews';
import { recipientFragments } from './features/productDisplay/Recipient';
import { reviewsNumberOfQuestionsFragments } from './features/productDisplay/ReviewsNumberOfQuestions';
import { skinToneFragments } from './features/filters/SkinTone';
import { skinTypeFragments } from './features/filters/SkinType';
import { skinConcernFragments } from './features/filters/SkinConcern';
import { skinGoalFragments } from './features/filters/SkinGoal';
import { skuCollectionFragments } from './features/SkuCollection';
import { skuUrlFragments } from './features/productDisplay/SkuUrl';
import { styleFragments } from './features/filters/Style';
import { subcatFragments } from './features/filters/Subcat';
import { subHeaderFragments } from './features/productDisplay/SubHeader';
import { descriptionStatementsFragments } from './features/productDisplay/descriptionStatements';
import { textureFragments } from './features/filters/Texture';
import { undertoneFragments } from './features/filters/Undertone';
import { usageFragments } from './features/productDisplay/Usage';
import { subDisplayNameFragments } from './features/productDisplay/SubDisplayName';
import { smartGiftFragments } from './features/tools/SmartGift';
import { vtoFoundationFragments } from './features/tools/VtoFoundation';
import { vtoMakeupFragments } from './features/tools/VtoMakeup';
import { freeFromFragments } from './features/filters/FreeFrom';
import { productSkinTypeFragments } from './features/filters/ProductSkinType';
import { shadeGroupsFragments } from './features/productDisplay/ShadeGroups';
import { keyIngredientsCollectionFragments } from './features/filters/KeyIngredientsCollection';
import { occasionProductFragments } from './features/filters/OccasionProduct';
import { fragranceNotesFragments } from './features/filters/FragranceNotes';
import { productBadgeImageFragments } from './features/productDisplay/ProductBadgeImage';
import { skuBadgeImageFragments } from './features/productDisplay/SkuBadgeImage';
import { shadeImageFragments } from './features/productDisplay/ShadeImage';
import { taxExcludedPriceFragments } from './features/priceDisplay/TaxExcludedPrice';
import { preorderFragments } from './features/productDisplay/Preorder';
import { productVideosFragments } from './features/productDisplay/ProductVideos';
import { vtoCrossSellFragments } from './features/tools/VtoCrossSell';
import { usageOptionsFragments } from './features/filters/UsageOptions';
import { skuTagsFragments } from './features/productDisplay/SkuTags';
import { formulatedWithoutFragments } from './features/productDisplay/FormulatedWithout';
import { discountsFragments } from './features/priceDisplay/Discounts';
import { skuMaincatFragments } from './features/filters/SkuMaincat';
import { skuPromoMessagingFragments } from './features/productDisplay/SkuPromoMessaging';
import { skuResponsiveImagesFragments } from './features/images/SkuResponsiveImages';
import { skuBenefitStatementFragments } from './features/productDisplay/SkuBenefitStatement';
import { brushTypesFragments } from './features/filters/BrushTypes';
import { brushShapesFragments } from './features/filters/BrushShapes';
import { formulatedWithoutProductFragments } from './features/productDisplay/FormulatedWithoutProduct';
import { sizeDescriptionFragments } from './features/productDisplay/SizeDescription';
import { expirationDateFragments } from './features/productDisplay/ExpirationDate';
import { productBenefitStatementFragments } from './features/productDisplay/ProductBenefitStatement';
import { productTimeOfUseFragments } from './features/productDisplay/ProductTimeOfUse';
import { productFragranceKeyNotesFragments } from './features/productDisplay/ProductFragranceKeyNotes';

export type IFeaturesFragments = {
    [key in Features | 'default' | 'mandatory']?: IQueryTypeFragments;
};

export type IQueryTypeFragments = {
    [key in QueryType]?: string;
};

interface IConfig {
    features: IFeatures;
}

export class QueryFragments {
    @observable private features: IFeatures;

    constructor(config: IConfig) {
        this.features = config.features;
    }

    @action
    public setFeatures = (features: IFeatures) => {
        this.features = features;
    };

    // get a list of strings representing all enabled features that come from config manager
    @computed
    get enabledFeatures() {
        const enabledFeatures: string[] = [];

        if (this.features) {
            Object.keys(this.features).forEach((featureKey) => {
                const feature = this.features && this.features[<Features>featureKey];
                if (feature && feature.enabled) {
                    enabledFeatures.push(featureKey);
                }
            });
        }

        return enabledFeatures;
    }

    @computed
    get discountTypes() {
        return (this.features && this.features.discounts && this.features.discounts.types) || [];
    }

    @computed
    get excludeTax() {
        return this.features && this.features.excludeTax && this.features.excludeTax.enabled;
    }

    // all available fragments for all brands
    public get fragments(): IFeaturesFragments {
        return {
            mandatory: mandatoryFragments(),
            default: defaultFragments(),
            productLargeImages: productLargeImagesFragments(),
            productMediumImages: productMediumImagesFragments(),
            productSmallImages: productSmallImagesFragments(),
            shadeGroups: shadeGroupsFragments(),
            skuLargeImages: skuLargeImagesFragments(),
            skuMediumImages: skuMediumImagesFragments(),
            skuSmallImages: skuSmallImagesFragments(),
            skuSmooshImages: skuSmooshImagesFragments(),
            skuModelImages: skuModelImagesFragments(),
            autoReplenish: autoReplenishFragments(),
            benefit: benefitFragments(),
            brand: brandFragments(),
            colorFamily: colorFamilyFragments(),
            descriptionStatements: descriptionStatementsFragments(),
            discounts: this.discountTypes.length
                ? discountsFragments(this.discountTypes, this.excludeTax)
                : undefined,
            engraving: engravingFragments(),
            excludeTax: taxExcludedPriceFragments(),
            expirationDate: expirationDateFragments(),
            finishOptions: finishOptionsFragments(),
            form: formFragments(),
            freeFrom: freeFromFragments(),
            fragranceFamily: fragranceFamilyFragments(),
            fragranceNotes: fragranceNotesFragments(),
            keyIngredients: keyIngredientsFragments(),
            keyIngredientsCollection: keyIngredientsCollectionFragments(),
            location: locationFragments(),
            maincat: maincatFragments(),
            mood: moodFragments(),
            occasion: occasionFragments(),
            occasionProduct: occasionProductFragments(),
            perlgem: perlgemFragments(),
            preOrder: preorderFragments(),
            productBadgeImage: productBadgeImageFragments(),
            productCollection: productCollectionFragments(),
            productCoverage: productCoverageFragments(),
            productCrossSell: productCrossSellFragments(),
            productFinish: productFinishFragments(),
            productReview: productReviewsFragments(),
            productSkinType: productSkinTypeFragments(),
            productBenefitStatement: productBenefitStatementFragments(),
            recipient: recipientFragments(),
            reviewsNumberOfQuestions: reviewsNumberOfQuestionsFragments(),
            sizeDescription: sizeDescriptionFragments(),
            skuBadgeImage: skuBadgeImageFragments(),
            skinConcern: skinConcernFragments(),
            skinGoal: skinGoalFragments(),
            skinTone: skinToneFragments(),
            skinType: skinTypeFragments(),
            skuCollection: skuCollectionFragments(),
            skuUrl: skuUrlFragments(),
            smartGift: smartGiftFragments(),
            style: styleFragments(),
            subcat: subcatFragments(),
            subDisplayName: subDisplayNameFragments(),
            subHeader: subHeaderFragments(),
            texture: textureFragments(),
            undertone: undertoneFragments(),
            usage: usageFragments(),
            vtoFoundation: vtoFoundationFragments(),
            vtoMakeup: vtoMakeupFragments(),
            vtoCrossSell: vtoCrossSellFragments(),
            shadeImage: shadeImageFragments(),
            finish: finishFragments(),
            productVideos: productVideosFragments(),
            usageOptions: usageOptionsFragments(),
            formulatedWithout: formulatedWithoutFragments(),
            formulatedWithoutProduct: formulatedWithoutProductFragments(),
            skuTags: skuTagsFragments(),
            skuMaincat: skuMaincatFragments(),
            skuPromoMessaging: skuPromoMessagingFragments(),
            skuResponsiveImages: skuResponsiveImagesFragments(),
            skuBenefitStatement: skuBenefitStatementFragments(),
            brushTypes: brushTypesFragments(),
            brushShapes: brushShapesFragments(),
            productTimeOfUse: productTimeOfUseFragments(),
            fragranceKeyNotes: productFragranceKeyNotesFragments()
        };
    }

    // dynamically construct a list with all enabled fragments based on enabled features and query type key
    @computed
    public get enabledFragments() {
        const availableFragments: { [key in string]?: string } = {
            product_default: defaultFragments()[QueryType.product],
            product__skus_default: defaultFragments()[QueryType.skus],
            product__collection: mandatoryFragments()[QueryType.collection]
        };

        Object.keys(this.fragments)
            .filter((key) => this.enabledFeatures.includes(key))
            .forEach((key) => {
                if (this.fragments[<Features>key]) {
                    const featureFragments: IQueryTypeFragments = <IQueryTypeFragments>(
                        this.fragments[<Features>key]
                    );

                    Object.keys(featureFragments).forEach((type) => {
                        availableFragments[`${<QueryType>type}_${key}`] =
                            featureFragments[<QueryType>type] || '';
                    });
                }
            });

        return availableFragments;
    }

    // dynamically construct a list of fragment manes using query type as a key
    @computed
    public get queryFragments() {
        const availableFragments: IQueryTypeFragments = {
            product: '...product_default ',
            product__skus: '...product__skus_default ',
            product_collection: '...product__collection '
        };

        this.enabledFeatures.forEach((feature) => {
            const featureFragments = this.fragments[<Features>feature] || [];

            Object.keys(featureFragments).forEach((type) => {
                availableFragments[<QueryType>type] = (
                    availableFragments[<QueryType>type] || ''
                ).concat(`...${type}_${feature} `);
            });
        });

        return availableFragments;
    }
}
